import React, { memo, useMemo, useState } from "react";
import { updateWidgetCache } from "../../../../util/Scripts";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";
import { KanbanScripts } from "./KanbanScripts";
import { Resizable } from "react-resizable";
import { Button, Popover } from "../../../BP2Components";
import KanbanItem from "./KanbanItem";
import { List } from "react-virtualized";
import * as ReactDOM from "react-dom";
import SumOfItems from "./SumOfItems";
import { Icon_Eye, Icon_Eye_Invisible } from "../../../BP2Components/Icons/icons";

const KanbanColumn = memo(
   ({
      columnId,
      column,
      columns,
      rows,
      settings,
      displayFormChildId,
      saveItemValue,
      setRows,
      actionButtons,
      previewItemFormChildId,
      viewSettings,
   }) => {
      const widgetCache = useSelector(
         ({ widgetCache }) =>
            (Array.isArray(widgetCache) &&
               widgetCache.find((widget) => widget.widgetId == settings.id)?.settings) || {
               columnsWidth: [],
               hiddenColumns: [],
            },
      );

      const [columnWidth, setColumnWidth] = useState(
         widgetCache.columnsWidth?.[columnId]?.width || 340,
      );
      const [isColumnResized, setIsColumnResized] = useState(false);

      const columnItems = useMemo(() => {
         if (!rows) return [];

         const colFieldKey = KanbanScripts.getTitleFromTechName(
            viewSettings.sortColumnsBy,
            columns,
         );

         return rows.filter((item) => {
            let element = item?.[colFieldKey];
            if (element && typeof element === "object" && "value" in element) {
               element = element.value;
            }
            return String(element) === String(column.name);
         });
      }, [rows, viewSettings.sortColumnsBy, columns, column.name]);

      ///creates a row that is used in column
      const getRowRender =
         (items) =>
         ({ index, style }) => {
            const item = items?.[index];

            if (!item) {
               return null;
            }
            return (
               <Draggable
                  key={item?.recordId ?? item?._id?.toString() ?? `item-${index}`}
                  draggableId={item?.recordId ?? item?._id?.toString() ?? `item-${index}`}
                  index={index}
               >
                  {(provided, snapshot) => {
                     return (
                        <div
                           ref={provided.innerRef}
                           {...provided.draggableProps}
                           {...provided.dragHandleProps}
                           className="draggable-item-kanban"
                           style={{
                              ...provided.draggableProps.style,
                              ...style,
                              backgroundColor: getColorForItem(item),
                              height: +style.height - 10,
                              //We need to make items a little bit narrower so it looks like that there is a margin
                              width:
                                 columnWidth -
                                 (columnItems.length * getElementHeight > 600 ? 20 : 5),
                           }}
                        >
                           <KanbanItem
                              key={index}
                              itemValue={item}
                              columns={columns}
                              rows={rows}
                              settings={settings}
                              displayFormChildId={displayFormChildId}
                              saveItemValue={saveItemValue}
                              setRows={setRows}
                              actionButtons={actionButtons}
                              previewItemFormChildId={previewItemFormChildId}
                           />
                        </div>
                     );
                  }}
               </Draggable>
            );
         };

      // p height: 22.5px, break-line in item: 9px, rowComponent-padding: 10px, rowComponent-margin: 5px
      // height = 22.5 * x + 9(x - 1) + 10 + 5 , x = settings.itemContent.length
      const getElementHeight =
         settings.itemContent.length > 0 ? 31.5 * settings.itemContent.length + 10 : 36;

      const onResize = (event, { size }, columnId) => {
         updateWidgetCache(settings.id, settings.name, {
            ...widgetCache,
            columnsWidth: {
               ...widgetCache.columnsWidth,
               [columnId]: { width: size?.width, percentage: null },
            },
         });
      };

      const getColorForItem = (item) => {
         let color = "";

         settings.verticalPlacementSettings?.every((setting) => {
            let valueFromItem =
               item?.[KanbanScripts.getTitleFromTechName(setting.columnTechName, columns)];
            if (valueFromItem?.value) {
               valueFromItem = valueFromItem.value;
            }
            if (valueFromItem === setting.rowLabel) {
               color = setting.color;
               return false;
            }
            return true;
         });
         return color;
      };

      return widgetCache.hiddenColumns.includes(columnId) ? (
         <Popover title={column.name} placement="top" trigger="hover" dark={true}>
            <Button
               buttonType="primary"
               onClick={() =>
                  KanbanScripts.handleHidingColumn(
                     settings.id,
                     settings.name,
                     columnId,
                     widgetCache,
                  )
               }
               className="kanban-column-show-icon"
            >
               <Icon_Eye_Invisible />
            </Button>
         </Popover>
      ) : (
         <Resizable
            width={columnWidth}
            onResize={(event, { size }) => setColumnWidth(size.width)}
            onResizeStart={() => setIsColumnResized(true)}
            onResizeStop={(event, { element, size, handle }) => {
               setIsColumnResized(false);
               onResize(event, { element, size, handle }, columnId);
            }}
            draggableOpts={{ grid: [5] }}
            key={columnId}
            className="kanban-column-container"
            style={{
               backgroundColor: settings?.horizontalPlacementSettings
                  ?.find((item) => item.columnTechName === viewSettings.sortColumnsBy)
                  ?.colors?.find((item) => item.label === column.name)?.columnColor,
            }}
         >
            <div>
               <Button
                  buttonType="default"
                  onClick={() =>
                     KanbanScripts.handleHidingColumn(
                        settings.id,
                        settings.name,
                        columnId,
                        widgetCache,
                     )
                  }
                  className="kanban-column-hide-icon"
               >
                  <Icon_Eye />
               </Button>
               <h6>{column.name}</h6>
               {isColumnResized ? (
                  <div className="kanban-column-dummy" style={{ width: columnWidth + 12 }}>
                     <h1 className="h1">{columnWidth + "px"}</h1>
                  </div>
               ) : (
                  <Droppable
                     droppableId={columnId}
                     mode="virtual"
                     key={columnId}
                     //That's a rendered element that is dragged.
                     renderClone={(provided, snapshot, rubric) => (
                        <div
                           ref={provided.innerRef}
                           {...provided.draggableProps}
                           {...provided.dragHandleProps}
                           className="draggable-item-kanban draggable-item-kanban-dragged"
                           style={{
                              ...provided.draggableProps.style,
                              backgroundColor: getColorForItem(columnItems[rubric.source.index]),
                           }}
                        >
                           <KanbanItem
                              key={rubric.source.index}
                              itemValue={columnItems[rubric.source.index]}
                              columns={columns}
                              rows={rows}
                              settings={settings}
                              displayFormChildId={displayFormChildId}
                              saveItemValue={saveItemValue}
                              setRows={setRows}
                              actionButtons={actionButtons}
                              previewItemFormChildId={previewItemFormChildId}
                           />
                        </div>
                     )}
                  >
                     {(provided, snapshot) => {
                        const itemCount = snapshot.isUsingPlaceholder
                           ? columnItems.length + 1
                           : columnItems.length;
                        return (
                           <div
                              className="kanban-column-list"
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              style={{
                                 background: snapshot.isDraggingOver
                                    ? "rgba(222, 222, 222, 0.8)"
                                    : "rgba(236, 236, 236, 0.8)",
                              }}
                           >
                              {/* TO FIX - still re-renders badly, because the values are sometimes the same */}
                              <List
                                 //This thing actually is forcing List to re-render itself, because the List component does not want to do that by itselft when we are changing value for width and rowHeight
                                 key={
                                    settings.itemContent.length +
                                    columnWidth +
                                    getElementHeight +
                                    columnId
                                 }
                                 height={600}
                                 rowCount={itemCount}
                                 rowHeight={getElementHeight}
                                 width={columnWidth}
                                 // Just don't touch it.
                                 ref={(ref) => {
                                    if (ref) {
                                       const whatHasMyLifeComeTo = ReactDOM.findDOMNode(ref);
                                       if (whatHasMyLifeComeTo instanceof HTMLElement) {
                                          provided.innerRef(whatHasMyLifeComeTo);
                                       }
                                    }
                                 }}
                                 rowRenderer={getRowRender(columnItems)}
                              />
                           </div>
                        );
                     }}
                  </Droppable>
               )}
               <SumOfItems column={column} columns={columns} settings={settings} />
            </div>
         </Resizable>
      );
   },
);

export default KanbanColumn;
